import React, { useState, useMemo, useCallback } from 'react';
import {
  Clock, File, Star, Tag, FolderOpen, Upload, FolderPlus,
  Grid, List, ChevronRight, Image, Video, FileText, Folder
} from 'lucide-react';
import { useFileStore } from '../stores/file-store';
import { useCategoryStore } from '../stores/category-store';
import FileGrid from '../components/files/FileGrid';
import UploadModal from '../components/files/UploadModal';
import CreateCategoryModal from '../components/categories/CreateCategoryModal';
import FileFilters from '../components/shared/FileFilters';
import { useI18n } from '../i18n';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useUIStore, FILE_TABS } from '../stores/ui-store';


const DashboardPage: React.FC = () => {
  const { files, favoriteFiles, fetchFiles } = useFileStore();
  const { categories, fetchCategories } = useCategoryStore();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const activeTab = useUIStore((state) => state.activeFileTab);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState<'name' | 'size' | 'type' | 'updatedAt'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();
  const { t } = useI18n();

  // 处理筛选器变化
  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // 处理排序
  const handleSort = useCallback((field: 'name' | 'size' | 'type' | 'updatedAt') => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  }, [sortField]);

  // 获取顶层分类（仅用于"全部"tab）
  const topLevelCategories = useMemo(() => {
    let result = [...(categories || [])];

    // 筛选：文件夹类型
    if (filters.folderType && filters.folderType !== 'all') {
      result = result.filter((c: any) => c.visibility_type === filters.folderType);
    }

    // 排序：公开 > 受限 > 私密
    const visibilityOrder: Record<string, number> = { 'public': 0, 'restricted_public': 1, 'private': 2 };
    result.sort((a: any, b: any) => {
      const orderA = visibilityOrder[a.visibility_type || 'private'] ?? 2;
      const orderB = visibilityOrder[b.visibility_type || 'private'] ?? 2;
      return orderA - orderB;
    });

    return result;
  }, [categories, filters.folderType]);

  // 根据Tab和筛选器筛选文件
  const filteredFiles = useMemo(() => {
    let result = [...files];

    // 1. 根据Tab筛选文件类型
    switch (activeTab) {
      case 'all':
        // "全部"tab：不做类型过滤
        break;
      case 'recent':
        // 最近更新的文件（按时间排序，不限天数）
        break;
      case 'video':
        result = result.filter(f => f.type === 'video');
        break;
      case 'image':
        result = result.filter(f => f.type === 'image');
        break;
      case 'document':
        // 文档包括：word文档、PDF、Excel、PPT、代码文件等
        result = result.filter(f =>
          ['document', 'pdf', 'spreadsheet', 'presentation', 'code'].includes(f.type)
        );
        break;
      default:
        break;
    }

    // 2. 应用时间筛选器
    if (filters.time && filters.time !== 'all') {
      const now = new Date();
      const fileDate = (file: typeof result[0]) => new Date(file.updatedAt || file.createdAt);

      switch (filters.time) {
        case 'today':
          result = result.filter(f => {
            const date = fileDate(f);
            return date.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          result = result.filter(f => fileDate(f) >= weekAgo);
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          result = result.filter(f => fileDate(f) >= monthAgo);
          break;
        case 'year':
          result = result.filter(f => fileDate(f).getFullYear() === now.getFullYear());
          break;
      }
    }

    // 3. 应用格式筛选器（文档类型）
    if (filters.format && filters.format !== 'all') {
      result = result.filter(f => {
        const ext = f.name.split('.').pop()?.toLowerCase() || '';
        switch (filters.format) {
          case 'word': return ['doc', 'docx', 'rtf'].includes(ext);
          case 'excel': return ['xls', 'xlsx', 'csv'].includes(ext);
          case 'ppt': return ['ppt', 'pptx'].includes(ext);
          case 'pdf': return ext === 'pdf';
          case 'code':
            return ['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'c', 'cpp', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'html', 'css', 'json', 'xml', 'yaml', 'yml', 'sh', 'sql'].includes(ext);
          case 'txt':
            return ['txt', 'md', 'markdown'].includes(ext);
          default: return true;
        }
      });
    }

    // 4. 应用图片格式筛选器（基于扩展名）
    if (filters.imageFormat && filters.imageFormat !== 'all' && activeTab === 'image') {
      result = result.filter(f => {
        const ext = f.name.split('.').pop()?.toLowerCase() || '';
        switch (filters.imageFormat) {
          case 'png':
            return ext === 'png';
          case 'jpg':
            return ['jpg', 'jpeg'].includes(ext);
          case 'gif':
            return ext === 'gif';
          case 'webp':
            return ext === 'webp';
          case 'other':
            return !['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
          default:
            return true;
        }
      });
    }

    // 5. 应用视频分辨率筛选器（基于文件名）
    if (filters.resolution && filters.resolution !== 'all' && activeTab === 'video') {
      result = result.filter(f => {
        const fileName = f.name.toLowerCase();
        switch (filters.resolution) {
          case '4k':
            return fileName.includes('4k') || fileName.includes('2160p') || fileName.includes('uhd');
          case '1080p':
            return fileName.includes('1080p') || fileName.includes('fhd');
          case '720p':
            return fileName.includes('720p') || fileName.includes('hd');
          case 'other':
            return !fileName.includes('4k') && !fileName.includes('2160p') &&
              !fileName.includes('1080p') && !fileName.includes('720p');
          default:
            return true;
        }
      });
    }

    // 5. 应用文件大小筛选器
    if (filters.size && filters.size !== 'all') {
      const MB = 1024 * 1024;
      result = result.filter(f => {
        const sizeInMB = f.size / MB;
        switch (filters.size) {
          case 'tiny': return sizeInMB < 1;
          case 'small': return sizeInMB >= 1 && sizeInMB < 10;
          case 'medium': return sizeInMB >= 10 && sizeInMB < 100;
          case 'large': return sizeInMB >= 100;
          default: return true;
        }
      });
    }

    // 6. 应用收藏状态筛选器
    if (filters.starred && filters.starred !== 'all') {
      result = result.filter(f => {
        if (filters.starred === 'starred') return f.starred === true;
        if (filters.starred === 'unstarred') return !f.starred;
        return true;
      });
    }

    // 根据排序字段排序
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'updatedAt':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [files, activeTab, sortField, sortOrder, filters]);

  // 分页/截断逻辑单独处理，确保统计数量正确
  const displayedFiles = useMemo(() => {
    // "全部"tab只显示10个文件，其他tab显示20个
    return filteredFiles.slice(0, activeTab === 'all' ? 10 : 20);
  }, [filteredFiles, activeTab]);

  // 统计数据
  const stats = [
    { label: t('totalFiles'), value: files.length, icon: File, color: 'text-blue-500', bgColor: 'bg-blue-50' },
    { label: t('starred'), value: favoriteFiles.length, icon: Star, color: 'text-amber-500', bgColor: 'bg-amber-50' },
    { label: t('categoriesLabel'), value: categories.length, icon: Tag, color: 'text-emerald-500', bgColor: 'bg-emerald-50' },
    { label: t('recent'), value: filteredFiles.length, icon: Clock, color: 'text-violet-500', bgColor: 'bg-violet-50' },
  ];

  // 获取当前Tab的图标
  const getTabIcon = (tabId: string) => {
    switch (tabId) {
      case 'video': return <Video size={14} />;
      case 'image': return <Image size={14} />;
      case 'document': return <FileText size={14} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-5">

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {stats.map((stat, i) => (
          <div
            key={i}
            className="flex items-center p-3 sm:p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
          >
            <div className={`p-2 sm:p-2.5 mr-2 sm:mr-3 rounded-lg ${stat.bgColor}`}>
              <stat.icon size={16} className={`${stat.color} sm:w-[18px] sm:h-[18px]`} />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-gray-500">{stat.label}</p>
              <p className="text-lg sm:text-xl font-bold text-gray-900">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* 文件列表区 */}
      <div className="bg-white rounded-xl border border-gray-100">
        {/* 操作栏 - 仿夸克设计 */}
        <div className="flex items-center justify-between px-3 sm:px-5 py-2.5 sm:py-3 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            <h2 className="text-sm sm:text-base font-medium text-gray-800">文件</h2>
          </div>

          <div className="flex items-center space-x-1.5 sm:space-x-2">
            {/* 操作按钮组 */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Upload size={14} className="sm:mr-1.5" />
              <span className="hidden sm:inline">上传文件</span>
            </button>
            <button
              onClick={() => setShowCreateCategoryModal(true)}
              className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FolderPlus size={14} className="sm:mr-1.5" />
              <span className="hidden sm:inline">新建文件夹</span>
            </button>

            {/* 分隔线 */}
            <div className="w-px h-5 bg-gray-200 mx-1 sm:mx-2" />

            {/* 视图切换 */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="列表视图"
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="网格视图"
              >
                <Grid size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* 筛选器区域 */}
        <div className="px-3 sm:px-5 py-2 sm:py-3 border-b border-gray-50 bg-gray-50/50">
          <FileFilters
            type={activeTab as 'all' | 'recent' | 'video' | 'image' | 'document'}
            filters={filters}
            onFilterChange={handleFilterChange}
            onRefresh={() => fetchFiles()}
          />
        </div>

        {/* 文件内容 */}
        <div className="p-3 sm:p-5">
          {activeTab === 'all' ? (
            /* "全部"tab：同时显示文件夹和文件 */
            viewMode === 'grid' ? (
              <div className="space-y-6">
                {/* 文件夹区域 */}
                {topLevelCategories.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">文件夹</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                      {topLevelCategories.map((category: any) => (
                        <div
                          key={category.id}
                          className="relative flex flex-col overflow-hidden transition-all bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md cursor-pointer group"
                          onClick={() => navigate(`/uadmin/categories/${category.id}`)}
                        >
                          <div className="relative aspect-video bg-gray-50 flex items-center justify-center">
                            <Folder size={36} className="text-teal-600" />
                          </div>
                          <div className="flex flex-col flex-1 p-3">
                            <h3 className="mb-1 text-sm font-medium text-gray-900 truncate" title={category.name}>{category.name}</h3>
                            <div className="mt-auto flex items-center justify-between text-xs text-gray-500">
                              <span>{t('folder')}</span>
                              <span>{category.file_count || 0} {t('filesLabel')}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {topLevelCategories.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    {t('noRecentFiles')}
                  </div>
                )}
              </div>
            ) : (
              /* 列表视图：先显示文件夹，再显示文件 */
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                {/* 可排序表头 */}
                <div className="grid grid-cols-12 gap-4 px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50">
                  <div className="col-span-5 flex items-center">文件名</div>
                  <div className="col-span-2 flex items-center">大小</div>
                  <div className="col-span-2 flex items-center">类型</div>
                  <div className="col-span-3 flex items-center">修改时间</div>
                </div>
                {/* 文件夹行 */}
                {topLevelCategories.map((category: any) => (
                  <div
                    key={category.id}
                    className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-gray-50 transition-colors cursor-pointer bg-white"
                    onClick={() => navigate(`/uadmin/categories/${category.id}`)}
                  >
                    <div className="col-span-5 flex items-center min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center mr-3 flex-shrink-0">
                        <FolderOpen size={16} className="text-teal-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-800 truncate">{category.name}</span>
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">
                      {(() => {
                        const totalSize = (category.files || []).reduce((sum: number, f: any) => sum + (f.size || 0), 0);
                        return totalSize > 0 ? `${(totalSize / 1024 / 1024).toFixed(1)} MB` : '-';
                      })()}
                    </div>
                    <div className="col-span-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-600">
                        {t('folder')}
                      </span>
                    </div>
                    <div className="col-span-3 text-sm text-gray-500">
                      {category.updated_at ? dayjs(category.updated_at).format('YYYY-MM-DD HH:mm') : (category.created_at ? dayjs(category.created_at).format('YYYY-MM-DD HH:mm') : '-')}
                    </div>
                  </div>
                ))}
                {/* 文件行 */}

              </div>
            )
          ) : (
            /* 其他tab：只显示文件 */
            <FileGrid
              files={displayedFiles}
              viewMode={viewMode}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              emptyMessage={
                activeTab === 'all'
                  ? t('noRecentFiles')
                  : `暂无${FILE_TABS.find(t => t.id === activeTab)?.label || ''}文件`
              }
            />
          )}
        </div>
      </div>

      {/* 底部快捷入口 - 紧凑横条设计 */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-2 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center space-x-0.5 sm:space-x-1">
          <span className="hidden sm:inline text-xs text-gray-400 mr-3">{t('quickAccess')}</span>
          {[
            { icon: File, label: t('allFiles'), path: '/uadmin/files' },
            { icon: Star, label: t('starred'), path: '/uadmin/starred' },
            { icon: Clock, label: t('recent'), path: '/uadmin/recent' },
            { icon: FolderOpen, label: t('categories'), path: '/uadmin/categories' },
          ].map((item, i) => (
            <button
              key={i}
              onClick={() => navigate(item.path)}
              className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              title={item.label}
            >
              <item.icon size={14} className="sm:mr-1.5 text-gray-400" />
              <span className="hidden sm:inline">{item.label}</span>
            </button>
          ))}
        </div>

        {/* 收藏提示 */}
        {favoriteFiles.length > 0 && (
          <button
            onClick={() => navigate('/uadmin/starred')}
            className="flex items-center text-xs sm:text-sm text-amber-600 hover:text-amber-700"
          >
            <Star size={14} className="sm:mr-1" />
            <span className="hidden sm:inline">{favoriteFiles.length} 个收藏</span>
            <span className="sm:hidden">{favoriteFiles.length}</span>
            <ChevronRight size={14} className="ml-0.5" />
          </button>
        )}
      </div>

      {/* Modals */}
      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} />
      )}
      {showCreateCategoryModal && (
        <CreateCategoryModal onClose={() => setShowCreateCategoryModal(false)} />
      )}
    </div>
  );
};

export default DashboardPage;